import React from 'react';
import type { PatientDetailsPatientProp } from './internal';
import {
  formattedBirthPlace,
  getFullGender,
  getValueOrUnknownString,
  PatientDetailsHorizontalFlexElement,
  PatientDetailsSection
} from './internal';
import { intl, intlLanguageCode } from '../../lib';
import type { PatientDetailsProps } from './PatientDetails';
import { PatientDetailsBirthdateInfo } from './PatientDetailsBirthdateInfo';

/**
 * Props for a {@link PatientDetailsPersonDetails} component.
 */
export type PatientDetailsPersonDetailsProps = PatientDetailsPatientProp &
  Readonly<
    Pick<PatientDetailsProps, 'ssnHidden' | 'motherMaidenNameHidden'>
  > & {
    readonly birthPlaceHidden?: boolean;
  };

/**
 * Component for displaying a person's personal details (name, gender, SSN, birthdate, etc.).
 *
 * @param properties a {@link PatientDetailsPersonDetailsProps} object
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function PatientDetailsPersonDetails({
  patient,
  ssnHidden,
  motherMaidenNameHidden,
  birthPlaceHidden,
  ...props
}: Readonly<PatientDetailsPersonDetailsProps>): JSX.Element {
  const birthPlace = patient.addresses?.find(addr => addr.addressType === 'BR');

  return (
    <PatientDetailsSection
      {...props}
      className="ehs-patient-details-person-details"
      // TODO: should we have a common rendering method for this? different places seem to do name rendering in a different way
      title={intl('patientsearchportlet.view.createPatient.demographics')}
    >
      <PatientDetailsBirthdateInfo patient={patient} />
      <PatientDetailsHorizontalFlexElement
        label={intl('view.patient.gender')}
        value={getValueOrUnknownString(getFullGender(patient.gender))}
      />
      {!ssnHidden && (
        <PatientDetailsHorizontalFlexElement
          label={intl('view.patient.ssn')}
          value={getValueOrUnknownString(patient.svnr)}
        />
      )}
      {!birthPlaceHidden && birthPlace && (
        <PatientDetailsHorizontalFlexElement
          label={intl('view.patient.birthplace')}
          value={getValueOrUnknownString(formattedBirthPlace(birthPlace))}
        />
      )}
      {!motherMaidenNameHidden && patient.name?.motherMaidenName && (
        <PatientDetailsHorizontalFlexElement
          label={intl('view.patient.mothermaidenname')}
          value={patient.name.motherMaidenName}
        />
      )}
      {patient.language && (
        <PatientDetailsHorizontalFlexElement
          label={intl('common.language')}
          value={intlLanguageCode(
            patient.language,
            window.SenseConstants?.language ?? 'en'
          )}
        />
      )}
    </PatientDetailsSection>
  );
}
